import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/trust-compliance", label: "Trust & Compliance" },
  { to: "/pricing", label: "Pricing" },
  { to: "/security", label: "Security" },
];

const segmentLinks = [
  { to: "/trust-attorneys", label: "Trust Attorneys" },
  { to: "/real-estate-attorneys", label: "Real Estate Attorneys" },
  { to: "/corporate-attorneys", label: "Corporate & Business" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [segOpen, setSegOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSegOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={[
        "fixed top-0 inset-x-0 z-50 transition-all duration-300 bg-white",
        scrolled
          ? "border-b border-[#0C1D36]/10 shadow-[0_1px_0_rgba(12,29,54,0.04)]"
          : "",
      ].join(" ")}
    >
      <div className="container-editorial flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo-link">
          <img
            src="/brand/usa-bookkeeping-logo.png"
            alt="USA Bookkeeping Solutions LLC"
            className="h-14 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {primaryLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
              className={({ isActive }) =>
                [
                  "text-sm font-medium link-underline transition-colors",
                  isActive ? "text-[#BA1A1A]" : "text-[#0C1D36] hover:text-[#BA1A1A]",
                ].join(" ")
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setSegOpen(true)}
            onMouseLeave={() => setSegOpen(false)}
          >
            <button
              className="text-sm font-medium text-[#0C1D36] hover:text-[#BA1A1A] inline-flex items-center gap-1 transition-colors"
              data-testid="nav-segments-trigger"
              onClick={() => setSegOpen((s) => !s)}
            >
              Who we serve <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {segOpen && (
              <div
                className="absolute right-0 top-full pt-3 w-72"
                data-testid="nav-segments-menu"
              >
                <div className="bg-white border border-[#0C1D36]/10 shadow-lg p-2">
                  {segmentLinks.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      className="block px-4 py-3 text-sm text-[#0C1D36] hover:bg-[#F3EFE9] hover:text-[#BA1A1A] transition-colors"
                      data-testid={`nav-segment-${s.to.replace("/", "")}`}
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" className="btn-primary" data-testid="navbar-cta">
            Request a Financial Review
          </Link>
        </div>

        <button
          className="lg:hidden text-[#0C1D36] p-2 -mr-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#0C1D36]/10 bg-white" data-testid="mobile-menu">
          <div className="container-editorial py-6 flex flex-col gap-1">
            {primaryLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-[#0C1D36] border-b border-[#0C1D36]/10 text-sm"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-4 pb-2 eyebrow-muted">Who we serve</div>
            {segmentLinks.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="py-3 text-[#0C1D36] border-b border-[#0C1D36]/10 text-sm"
                data-testid={`mobile-seg-${s.to.replace("/", "")}`}
              >
                {s.label}
              </Link>
            ))}
            <Link to="/contact" className="btn-primary mt-6 justify-center w-full" data-testid="mobile-cta">
              Request a Financial Review
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
